import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { sampleInvoice } from '../constants/invoice'
import { ensureInvoiceId } from '../utils/invoiceCalculations'
import type { SyncedInvoice } from '../types/invoice'

// Import all slices
import {
    createInvoiceEditSlice,
    createInvoiceManagementSlice,
    createInvoiceSyncSlice,
    createSettingsSlice,
    createStoreManagementSlice,
    createInvoiceFilterSlice
} from './slices'

// Import slice interfaces
import type {
    InvoiceEditSlice,
    InvoiceManagementSlice,
    InvoiceSyncSlice,
    SettingsSlice,
    StoreManagementSlice,
    InvoiceFilterSlice
} from './slices'

// Define the complete store type
export interface InvoiceStore extends
    InvoiceEditSlice,
    InvoiceManagementSlice,
    InvoiceSyncSlice,
    SettingsSlice,
    StoreManagementSlice,
    InvoiceFilterSlice {
    // Core state
    currentInvoice: SyncedInvoice
    savedInvoices: SyncedInvoice[]
    subtotal: string
    vat: string
    total: string
    discountAmount: string
}

// Create the store with all slices
export const useInvoiceStore = create<InvoiceStore>()(
    devtools(
        persist(
            (...a) => {
                // For initial store creation, we use the sample invoice directly
                // The newInvoice function will be used for subsequent invoice creations
                const initialInvoice = ensureInvoiceId(sampleInvoice) as SyncedInvoice;

                return {
                    // Initial state
                    currentInvoice: initialInvoice,
                    savedInvoices: [],
                    subtotal: '0.00',
                    vat: '0.00',
                    total: '0.00',
                    discountAmount: '0.00',

                    // Add all slices
                    ...createInvoiceEditSlice(...a),
                    ...createInvoiceManagementSlice(...a),
                    ...createInvoiceSyncSlice(...a),
                    ...createSettingsSlice(...a),
                    ...createStoreManagementSlice(...a),
                    ...createInvoiceFilterSlice(...a),
                }
            },
            {
                name: 'invoice-store',
                version: 3, // Increment version for new migration
                migrate: (persistedState: unknown, version: number) => {
                    console.log('[Store] Migrating from version:', version)

                    if (version < 3) {
                        const state = persistedState as Partial<InvoiceStore>

                        // Ensure all invoices have IDs and synced status
                        return {
                            ...state,
                            invoices: state.invoices?.map(invoice => ensureInvoiceId(invoice) as SyncedInvoice) || [],
                            savedInvoices: state.savedInvoices?.map(invoice => ensureInvoiceId(invoice) as SyncedInvoice) || [],
                            currentInvoice: state.currentInvoice
                                ? ensureInvoiceId(state.currentInvoice) as SyncedInvoice
                                : ensureInvoiceId(sampleInvoice) as SyncedInvoice
                        }
                    }
                    return persistedState
                },
            }
        )
    )
)

// Re-export selectors from the selectors file
export * from './selectors' 